import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Subscription } from "./useSubscription";
import { useTestClock } from "@/contexts/TestClockContext";
import { safeLog } from '@/utils/safeLogger';

export interface CreditStatistics {
  totalUsed: number;
  usedLast7Days: number;
  usedThisPeriod: number;
  currentBalance: number | null;
  isLoading: boolean;
}

export function useCreditStatistics(subscription: Subscription | null) {
  const { getCurrentTime } = useTestClock();
  const [stats, setStats] = useState<CreditStatistics>({
    totalUsed: 0,
    usedLast7Days: 0,
    usedThisPeriod: 0,
    currentBalance: null,
    isLoading: true,
  });

  useEffect(() => {
    loadStatistics();

    // Set up real-time subscription for credit transaction changes
    const channel = supabase
      .channel('credit-stats-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'credit_transactions'
        },
        (payload) => {
          console.log('[useCreditStatistics] New transaction detected, reloading stats');
          // Reload stats after a short delay to ensure transaction is committed
          setTimeout(() => loadStatistics(), 300);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [subscription]);

  async function loadStatistics() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('[useCreditStatistics] No user found');
        return;
      }

      console.log('[useCreditStatistics] Loading stats for user:', user.id);
      console.log('[useCreditStatistics] Subscription period start:', subscription?.current_period_start);

      // Parallel queries for better performance
      const [allTransactions, last7DaysTransactions, currentPeriodTransactions, latestTransaction] = await Promise.all([
        // Total used (all time, excluding credits/topups)
        supabase
          .from('credit_transactions')
          .select('final_credits, action_type')
          .eq('user_id', user.id)
          .neq('action_type', 'topup')
          .neq('action_type', 'subscription')
          .neq('action_type', 'subscription_renewal')
          .neq('action_type', 'subscription_purchase'),
        
        // Last 7 days (using Test Clock if active)
        supabase
          .from('credit_transactions')
          .select('final_credits')
          .eq('user_id', user.id)
          .gte('created_at', new Date(getCurrentTime().getTime() - 7 * 24 * 60 * 60 * 1000).toISOString())
          .gt('final_credits', 0)
          .neq('action_type', 'topup')
          .neq('action_type', 'subscription')
          .neq('action_type', 'subscription_renewal')
          .neq('action_type', 'subscription_purchase'),
        
        // Current billing period (if subscription exists)
        subscription?.current_period_start
          ? supabase
              .from('credit_transactions')
              .select('final_credits')
              .eq('user_id', user.id)
              .gte('created_at', subscription.current_period_start)
              .gt('final_credits', 0)
              .neq('action_type', 'topup')
              .neq('action_type', 'subscription')
              .neq('action_type', 'subscription_renewal')
              .neq('action_type', 'subscription_purchase')
          : Promise.resolve({ data: null, error: null }),
        
        // Latest transaction to get current balance
        supabase
          .from('credit_transactions')
          .select('credits_after')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
      ]);

      console.log('[useCreditStatistics] Current period transactions:', currentPeriodTransactions);
      console.log('[useCreditStatistics] Latest transaction:', latestTransaction);

      // Calculate totals
      const totalUsed = (allTransactions.data || [])
        .reduce((sum, t) => sum + t.final_credits, 0);

      const usedLast7Days = (last7DaysTransactions.data || [])
        .reduce((sum, t) => sum + t.final_credits, 0);

      const usedThisPeriod = (currentPeriodTransactions.data || [])
        .reduce((sum, t) => sum + t.final_credits, 0);

      console.log('[useCreditStatistics] Calculated usedThisPeriod:', usedThisPeriod);

      // Use credits_after from latest transaction as the true current balance
      const currentBalance = latestTransaction.data?.credits_after ?? subscription?.credits_remaining ?? null;

      setStats({
        totalUsed,
        usedLast7Days,
        usedThisPeriod,
        currentBalance,
        isLoading: false,
      });

    } catch (error) {
      console.error('Error loading credit statistics:', error);
      setStats(prev => ({ ...prev, isLoading: false }));
    }
  }

  return { ...stats, refresh: loadStatistics };
}
